/**
 * Reads rate-limit hints from a response. Live Automaid sends IETF `ratelimit-*` (delta-seconds
 * reset) plus `x-rate-limit-*`; the spec documents `X-RateLimit-*` (epoch reset). None are exposed
 * cross-origin today, so every field may be null in the browser.
 */
export interface RateLimitInfo {
  limit: number | null;
  remaining: number | null;
  /** seconds until the window resets */
  resetSeconds: number | null;
  /** from Retry-After (seconds or HTTP-date), in ms */
  retryAfterMs: number | null;
}

function firstNumber(value: string | null): number | null {
  if (!value) return null;
  const first = value.split(',')[0]?.trim() ?? '';
  const n = Number.parseFloat(first);
  return Number.isFinite(n) ? n : null;
}

function pick(headers: Headers, names: string[]): string | null {
  for (const name of names) {
    const v = headers.get(name);
    if (v !== null && v !== '') return v;
  }
  return null;
}

export function readRateLimit(headers: Headers, now: () => number = Date.now): RateLimitInfo {
  const limit = firstNumber(pick(headers, ['ratelimit-limit', 'x-ratelimit-limit', 'x-rate-limit-limit']));
  const remaining = firstNumber(
    pick(headers, ['ratelimit-remaining', 'x-ratelimit-remaining', 'x-rate-limit-remaining']),
  );
  let resetSeconds = firstNumber(pick(headers, ['ratelimit-reset', 'x-ratelimit-reset', 'x-rate-limit-reset']));
  // Values that look like a UTC epoch (spec wording) become a delta.
  if (resetSeconds !== null && resetSeconds > 1_000_000_000) {
    resetSeconds = Math.max(0, Math.round(resetSeconds - now() / 1000));
  }
  let retryAfterMs: number | null = null;
  const retryAfter = headers.get('retry-after');
  if (retryAfter) {
    const secs = Number.parseFloat(retryAfter);
    if (Number.isFinite(secs)) retryAfterMs = Math.max(0, Math.round(secs * 1000));
    else {
      const at = Date.parse(retryAfter);
      if (Number.isFinite(at)) retryAfterMs = Math.max(0, at - now());
    }
  }
  return { limit, remaining, resetSeconds, retryAfterMs };
}
